import { useEffect } from 'react';
import type { FC } from 'react';
import './HeaderNotificationDrawer.scss';
import HeaderNotificationRowItem from './components/header-notification-row-item/HeaderNotificationRowItem.tsx';
import type {
	HeaderNotificationRowItemResponse,
	NotificationsData,
	NotificationTimeTranslations,
} from '../../../../../../../../components/blackrock/advisor-center/_types/header.ts';
import EmptyState from '../../../../../../../../components/blackrock/advisor-center/empty-state/EmptyState.tsx';
import useNotificationApi from '../../../../_helpers/hooks/useNotificationApi.ts';
import LoadIndicator from '../../../../../../../../components/blackrock/advisor-center/load-indicator/LoadIndicator.tsx';
import MarkAllButton from './components/mark-all-button/MarkAllButton.tsx';
import { MenuItem } from '../../../../../../../../components/blackrock/advisor-center/_types/navigation.ts';

type Props = {
	apiResult: ReturnType<typeof useNotificationApi>;
	notificationsData: NotificationsData;
	id?: string;
	closeDrawer?: (focusBellButton?: boolean) => void;
	isLoggedIn: boolean;
	signInMenuItem?: MenuItem;
};

const HeaderNotificationDrawer: FC<Props> = (props) => {
	const { isError, isEmpty, loadNotifications } = props.apiResult;

	useEffect(() => {
		loadNotifications();
	}, []);

	const { settingsItem } = props.notificationsData;

	return (
		<div className="header-notification-drawer" id={props.id}>
			<header>
				<div className="left-header-content">
					<div className="heading">
						{props.notificationsData?.translationMap?.drawerTitle}
					</div>

					{props.isLoggedIn && (
						<MarkAllButton {...props} forceDisability={isEmpty || isError} />
					)}
				</div>

				{props.isLoggedIn && (
					<a
						className="settings-icon-link"
						href={settingsItem?.href}
						data-final-url={true}
						tabIndex={0}
						aria-label={settingsItem?.ariaLabel}
						target={settingsItem?.target ?? undefined}
						data-link-event={settingsItem?.dataLinkEvent}
					>
						<i
							className={
								'settings-icon ' +
								(settingsItem?.icon?.path ?? 'fa fa-sharp fa-solid fa-gear')
							}
						/>
					</a>
				)}

				<i
					className="mobile-close-icon fa fa-regular fa-remove"
					onClick={() => props.closeDrawer?.()}
				/>

				<svg
					className="drawer-extension-triangle"
					width="26"
					height="14"
					viewBox="0 0 26 14"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
				>
					<path
						d="M23 11.5H24.2434L23.3461 10.6392L13.3461 1.04543L13 0.713347L12.6539 1.04543L2.65385 10.6392L1.7566 11.5H3H23Z"
						fill="white"
						stroke="black"
					/>
					<path d="M24 12L23 11H3L2 12H24Z" fill="white" />
				</svg>
			</header>

			<div className="notification-list">
				<NotificationList {...props} isEmpty={isEmpty} isError={isError} />
			</div>
		</div>
	);
};

export default HeaderNotificationDrawer;

type NotificationListProps = Props & {
	isError: boolean;
	isEmpty: boolean;
};

const NotificationList: FC<NotificationListProps> = ({
	apiResult,
	notificationsData: {
		translationMap: { timeTranslations, signInText },
		networkErrorConfig,
		noResultConfig,
		settingsItem,
	},
	isLoggedIn,
	isEmpty,
	isError,
	signInMenuItem,
}) => {
	if (!isLoggedIn) {
		return (
			<HeaderNotificationRowItem
				content={signInText}
				icon="fal fa-light fa-bell"
				link={signInMenuItem?.href}
				state="NEW"
				timeTranslations={timeTranslations}
			/>
		);
	}

	if (isError) {
		return (
			<EmptyState
				{...networkErrorConfig}
				callToAction={{
					...networkErrorConfig.callToAction,
					ctaCallback: apiResult.retry,
				}}
			/>
		);
	}

	if (apiResult.isLoading) {
		return (
			<div className="loader-wrap">
				<LoadIndicator />
			</div>
		);
	}

	if (isEmpty) {
		return (
			<EmptyState
				{...noResultConfig}
				callToAction={{
					...noResultConfig.callToAction,
					ctaCallback: () => {
						if (!settingsItem?.href) {
							return;
						}

						if (settingsItem?.target === '_blank') {
							window.open(settingsItem.href, '_blank')?.focus();
						} else {
							window.location.href = settingsItem.href;
						}
					},
					ariaLabel: settingsItem.ariaLabel ?? '',
					dataLinkEvent: settingsItem.dataLinkEvent ?? '',
				}}
			/>
		);
	}

	if (!apiResult.notifications) {
		return null;
	}

	return (
		<>
			<NotificationCategory
				notificationsInCategory={apiResult.notifications.latest}
				timeTranslations={timeTranslations}
			/>

			<NotificationCategory
				categoryLabel={timeTranslations.lastWeek}
				notificationsInCategory={apiResult.notifications.lastWeek}
				timeTranslations={timeTranslations}
			/>

			<NotificationCategory
				categoryLabel={timeTranslations.lastMonth}
				notificationsInCategory={apiResult.notifications.lastMonth}
				timeTranslations={timeTranslations}
			/>
		</>
	);
};

type NotificationCategoryProps = {
	categoryLabel?: string;
	notificationsInCategory: HeaderNotificationRowItemResponse[];
	timeTranslations: NotificationTimeTranslations;
};

const NotificationCategory: FC<NotificationCategoryProps> = ({
	categoryLabel,
	notificationsInCategory,
	timeTranslations,
}) => {
	if (!notificationsInCategory.length) return null;

	return (
		<>
			{!!categoryLabel && (
				<div className="notification-category-label">{categoryLabel}</div>
			)}

			{notificationsInCategory.map((notification, index) => (
				<HeaderNotificationRowItem
					key={notification.timestamp + index}
					timeTranslations={timeTranslations}
					{...notification}
					link={`${notification.link}?notification=${notification.id}+${notification.communicationId}`}
				/>
			))}
		</>
	);
};
